import type {
  Asset,
  AssetTransferPlan,
  PropertyDeed,
  TransferMechanism,
  TransferPlanStatus,
} from "src/types/snugtotal";

export const transferMechanismLabels: Record<TransferMechanism, string> = {
  TRUST_FUNDING: "Living Trust",
  POD: "POD",
  TOD: "TOD",
  BENEFICIARY_DESIGNATION: "Beneficiary",
  TOD_DEED: "TOD Deed",
  JOINT_OWNERSHIP: "Survivorship",
  PROBATE: "Probate",
  OTHER: "Other",
};

// Rules-based suggestion for the intended mechanism. Only ever a suggestion —
// nothing is stored until the user picks one.
export const suggestMechanism = (
  asset: Pick<Asset, "asset_type" | "account_subtype">,
  hasTrust: boolean
): TransferMechanism => {
  switch (asset.asset_type) {
    case "realestate":
    case "business_interest":
      return hasTrust ? "TRUST_FUNDING" : "PROBATE";
    case "insurance_asset":
      return "BENEFICIARY_DESIGNATION";
    case "financial_account":
      return asset.account_subtype === "RETIREMENT" ||
        asset.account_subtype === "INVESTMENT"
        ? "BENEFICIARY_DESIGNATION"
        : "POD";
    case "personal_property":
    default:
      return hasTrust ? "TRUST_FUNDING" : "PROBATE";
  }
};

// The asset's primary transfer plan: newest row that hasn't been marked
// not-applicable. (The API returns plans newest-first.)
export const primaryTransferPlan = (
  plans: AssetTransferPlan[],
  assetId: string | null | undefined
): AssetTransferPlan | null => {
  if (!assetId) return null;
  return (
    plans.find(
      (plan) => plan.asset === assetId && plan.status !== "NOT_APPLICABLE"
    ) || null
  );
};

// Plain-language "how to actually do this" guidance, keyed by mechanism with
// per-asset-type overrides where the steps differ. Snug-service offers (e.g.
// quitclaim deed preparation) are layered on top by the UI, gated on the
// household's level of service — extend there as new services ship.
const TRUST_FUNDING_INSTRUCTIONS: Partial<Record<string, string>> & {
  default: string;
} = {
  realestate:
    "Real estate is transferred into a trust by recording a new deed with your county that names the trust as owner.",
  financial_account:
    "Contact your bank or brokerage to retitle the account in the name of your trust — most institutions have a standard form for this.",
  business_interest:
    "Assign your ownership interest to the trust with an assignment of interest; check your operating or partnership agreement for transfer requirements.",
  insurance_asset:
    "Ask your carrier to name the trust as the policy's beneficiary (or owner, depending on your goals).",
  personal_property:
    "Personal property is typically assigned to the trust with a general assignment document — your trust package may include one.",
  default:
    "Retitle this asset so the trust is named as its owner; assets titled in the trust pass outside of probate.",
};

const MECHANISM_INSTRUCTIONS: Record<TransferMechanism, string> = {
  TRUST_FUNDING: TRUST_FUNDING_INSTRUCTIONS.default,
  BENEFICIARY_DESIGNATION:
    "Set or update the beneficiary on file with your institution or carrier — ask for a beneficiary designation form and keep the confirmation.",
  POD: "Ask your bank to add a payable-on-death (POD) designation to this account — usually a quick signature-card update.",
  TOD: "Ask your institution to add a transfer-on-death (TOD) registration to this account.",
  TOD_DEED:
    "Record a transfer-on-death deed with your county (available in many states) naming who receives the property automatically at death.",
  JOINT_OWNERSHIP:
    "Title the asset jointly with right of survivorship so the surviving owner takes it automatically. Retitling can have gift and tax implications — consider getting advice first.",
  PROBATE:
    "No setup needed — this asset will pass under your will through the probate process.",
  OTHER:
    "Document how this asset is meant to transfer and keep the supporting paperwork with your estate records.",
};

export const transferInstructions = (
  mechanism: TransferMechanism,
  assetType: Asset["asset_type"] | null | undefined
): string => {
  if (mechanism === "TRUST_FUNDING") {
    return (
      TRUST_FUNDING_INSTRUCTIONS[assetType || ""] ||
      TRUST_FUNDING_INSTRUCTIONS.default
    );
  }
  return MECHANISM_INSTRUCTIONS[mechanism];
};

// The asset's current deed: the one that hasn't been replaced or voided.
export const currentPropertyDeed = (
  deeds: PropertyDeed[],
  assetId: string | null | undefined
): PropertyDeed | null => {
  if (!assetId) return null;
  return (
    deeds.find(
      (deed) =>
        deed.asset === assetId &&
        deed.status !== "SUPERSEDED" &&
        deed.status !== "VOID"
    ) || null
  );
};

// The deed lifecycle read as transfer-plan status: a requested deed is trust
// funding in progress; a recorded one is done. Mirrors the backend's
// sync_deed_transfer_plan so the UI never lags the deed.
export const deedTransferStatus = (
  deed: PropertyDeed | null | undefined
): TransferPlanStatus | null => {
  switch (deed?.status) {
    case "RECORDED":
      return "COMPLETED";
    case "REQUESTED":
    case "GENERATED":
    case "SIGNED":
    case "NOTARIZED":
      return "IN_PROGRESS";
    default:
      return null;
  }
};

// The mechanisms a user can pick from, in display order. Shared by the
// drawer's Transfer tab and the Assets page row pill so both offer the same
// choices.
export const transferMechanismOptions: TransferMechanism[] = [
  "TRUST_FUNDING",
  "BENEFICIARY_DESIGNATION",
  "POD",
  "TOD",
  "TOD_DEED",
  "JOINT_OWNERSHIP",
  "PROBATE",
  "OTHER",
];

// Status tone of the row pill — mirrors the Documents page checklist:
//   unknown   nothing chosen, or chosen but not set up yet (gray)
//   progress  chosen, work underway (blue)
//   complete  set up, or intentional probate which needs no setup (green)
export type TransferChipKind = "unknown" | "progress" | "complete";

export interface TransferChipState {
  // The chosen mechanism's name, or "Not chosen".
  label: string;
  kind: TransferChipKind;
  mechanism: TransferMechanism | null;
}

// The row pill: the label is purely the chosen transfer vehicle, the color is
// purely its status. Nothing chosen reads "Not chosen" — probate is never
// asserted on the user's behalf.
//
// A requested deed IS the trust-funding plan in motion, so when the asset has
// one it drives the pill regardless of what the plan row says.
export const transferChipState = (
  plan: AssetTransferPlan | null | undefined,
  deed?: PropertyDeed | null
): TransferChipState => {
  const deedStatus = deedTransferStatus(deed);
  const effective: { mechanism: TransferMechanism; status: TransferPlanStatus } | null =
    deedStatus
      ? { mechanism: "TRUST_FUNDING", status: deedStatus }
      : plan
      ? { mechanism: plan.mechanism, status: plan.status }
      : null;
  if (!effective) {
    return { label: "Not chosen", kind: "unknown", mechanism: null };
  }

  const { mechanism, status } = effective;
  // A deed in motion reads as the step the user is actually on — "Quitclaim
  // Deed" — and only becomes "Living Trust" once they confirm it's recorded.
  const label =
    deedStatus === "IN_PROGRESS"
      ? "Quitclaim Deed"
      : transferMechanismLabels[mechanism];

  // Intentional probate needs no setup — it's simply the confirmed default.
  if (mechanism === "PROBATE" || status === "COMPLETED") {
    return { label, kind: "complete", mechanism };
  }
  if (status === "IN_PROGRESS") {
    return { label, kind: "progress", mechanism };
  }
  return { label, kind: "unknown", mechanism };
};
